import React, { useMemo } from 'react';
import { Form, Select } from 'antd';
import { useGeneralFormDependencies } from 'client/modules/cbi-entity/services/general-info/hooks/useGeneralFormDependencies';
import { DefaultOptionType } from 'antd/lib/select';
import { Dataset } from '@cbinsights/externalapiservice/externalapiservice';
import {
  DataFeedFirmographicsFilters,
  DataFeedFormField,
} from './DataFeedForm';

export const SectorIndustrySelector = ({
  filterName,
}: {
  filterName: string[];
}) => {
  const { data, isFetching } = useGeneralFormDependencies({
    enabled: true,
  });
  const form = Form.useFormInstance();
  const sectorMap = data?.allSectorValues?.taxonomyMap || [];

  const sectorOptions = useMemo(
    () =>
      sectorMap.map((sector) => ({
        value: sector.id,
        label: sector.name,
      })),
    [sectorMap]
  );

  const currentSector: DefaultOptionType[] = Form.useWatch(
    [...filterName, DataFeedFirmographicsFilters.sectors],
    form
  );
  const currentIndustries: DefaultOptionType[] = Form.useWatch(
    [...filterName, DataFeedFirmographicsFilters.industries],
    form
  );

  const industriesMap = useMemo(() => {
    return sectorMap
      .filter((sector) =>
        currentSector?.map((el) => el.value)?.includes(sector.id)
      )
      .flatMap((sector) => sector.children || []);
  }, [sectorMap, currentSector]);

  const industriesOptions = useMemo(
    () =>
      industriesMap.map((industry) => ({
        value: industry.id,
        label: industry.name,
      })),
    [industriesMap]
  );

  const subIndustriesMap = useMemo(() => {
    return industriesMap
      .filter((industry) =>
        currentIndustries?.map((el) => el.value)?.includes(industry.id)
      )
      .flatMap((industry) => industry.children || []);
  }, [industriesMap, currentIndustries]);

  const subIndustriesOptions = useMemo(
    () =>
      subIndustriesMap.map((subIndustry) => ({
        value: subIndustry.id,
        label: subIndustry.name,
      })),
    [subIndustriesMap]
  );

  const handleSectorChange = () => {
    form.setFieldsValue({
      [DataFeedFormField.filters]: {
        [Dataset.FIRMOGRAPHICS]: {
          [DataFeedFirmographicsFilters.industries]: [],
          [DataFeedFirmographicsFilters.subIndustries]: [],
        },
      },
    });
  };

  return (
    <>
      <Form.Item
        name={[...filterName, DataFeedFirmographicsFilters.sectors]}
        label="Sectors"
      >
        <Select
          placeholder="Select sectors"
          className="min-w-[200px]"
          mode="multiple"
          allowClear
          loading={isFetching}
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
          options={sectorOptions}
          onChange={handleSectorChange}
          labelInValue
        />
      </Form.Item>

      <Form.Item
        name={[...filterName, DataFeedFirmographicsFilters.industries]}
        label="Industries"
        dependencies={[[...filterName, DataFeedFirmographicsFilters.sectors]]}
      >
        <Select
          placeholder="Select industries"
          className="min-w-[200px]"
          mode="multiple"
          allowClear
          loading={isFetching}
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
          options={industriesOptions}
          labelInValue
        />
      </Form.Item>

      <Form.Item
        name={[...filterName, DataFeedFirmographicsFilters.subIndustries]}
        label="Sub-industries"
        dependencies={[
          [...filterName, DataFeedFirmographicsFilters.industries],
        ]}
      >
        <Select
          placeholder="Select sub-industries"
          className="min-w-[200px]"
          mode="multiple"
          allowClear
          loading={isFetching}
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
          options={subIndustriesOptions}
          labelInValue
        />
      </Form.Item>
    </>
  );
};
